import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useCommHost } from './useCommHost';
import { commMemberLabel, commStudentLink, type CommAnswer, type CommItem } from '../services/realtime/commClient';

/**
 * 수업 프레젠(칠판 화면) 쪽 소통방 연결 — 저장 전에도 '질문 QR'로 학생 기기를 받기 위한 방.
 * - 방은 이 기기(localStorage)의 교사 소통방을 그대로 사용(리모컨 '소통 프레젠테이션'과 같은 방).
 * - 프레젠을 열기만 해서는 아무것도 하지 않는다. 이번 세션에서 '질문 QR'을 눌러 activated=true 가 된 뒤에만
 *   연결하고 명단(번호별 입장번호 해시용)을 보낸다. 명단이 바뀌면(서명 비교) 그때만 다시 보낸다.
 * - 학급 명단이 있으면 번호별 입장번호를 서버에 보내 '번호 + 입장번호'로 입장(서버에는 해시만 저장, 이름은 보내지 않음).
 * - 리모컨 질문을 학생 기기로 전달하고, 학생 응답을 '번호 + (교사 쪽 명단 이름)'으로 모아 화면에 표시/리모컨에 중계.
 */
export type LessonCommQuestionSnapshot = { question: LessonCommQuestion; answers: CommAnswer[] };
export type LessonCommRosterEntry = { no: number; entry: string };
export type LessonCommQuestion = {
  id: string;
  text: string;
  questionType: 'short' | 'ox' | 'choice' | 'essay';
  choices?: string[];
};

const ENTRY_RE = /^\d{6}$/;
const randomEntryCode = () => {
  const b = new Uint32Array(1);
  crypto.getRandomValues(b);
  return String(b[0] % 1_000_000).padStart(6, '0');
};

export function useLessonCommRoom(params: {
  canUse: boolean;
  /** 방 만들 때 반 이름 기본값 */
  className: string;
  /** 지금 열린 수업 프레젠 키 — 학생 화면은 이 프레젠에서 보낸 질문만 표시 */
  presKey?: string;
  /** 학급 명단(번호 → 개인 입장번호) — 비면 방 입장 코드로 입장 */
  roster: LessonCommRosterEntry[];
  /** 번호 → 학생 이름(교사 기기 명단). 화면 표시용으로만 쓰고 서버·학생에게 보내지 않음 */
  rosterNames?: ReadonlyMap<number, string>;
  /** 이번 세션에서 질문 QR 을 눌렀는지 — false 면 연결·명단 전송 안 함 */
  activated: boolean;
  /** 질문이 닫히거나 다음 질문으로 바뀔 때 (응답 일괄 저장용) */
  onQuestionEnded?: (snap: LessonCommQuestionSnapshot) => void;
  /** 학생 응답 도착 (현재 질문 id 기준) — 리모컨 중계용 */
  onStudentAnswer?: (questionId: string, answer: CommAnswer) => void;
}) {
  const { canUse, className, roster, activated, rosterNames } = params;
  const presKey = (params.presKey ?? '').slice(0, 80);
  const onStudentAnswerRef = useRef(params.onStudentAnswer);
  onStudentAnswerRef.current = params.onStudentAnswer;
  const onQuestionEndedRef = useRef(params.onQuestionEnded);
  onQuestionEndedRef.current = params.onQuestionEnded;

  const [userId, setUserId] = useState<string | null>(null);
  const [teacherName, setTeacherName] = useState('');
  useEffect(() => {
    let alive = true;
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!alive) return;
        const u = data.session?.user;
        setUserId(u?.id ?? null);
        const md = (u?.user_metadata ?? {}) as Record<string, unknown>;
        const nm = [md.full_name, md.name, md.nickname].find((v) => typeof v === 'string' && v.trim());
        setTeacherName(typeof nm === 'string' ? nm.trim().slice(0, 30) : '');
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  /* 질문 QR 을 누른 뒤에만 연결 (프레젠 열기만으로는 연결·전송 없음) */
  const host = useCommHost(userId, canUse, activated);
  const { room, connected, send, members, handlersRef } = host;

  const [question, setQuestion] = useState<LessonCommQuestion | null>(null);
  const questionRef = useRef<LessonCommQuestion | null>(null);
  questionRef.current = question;
  const [answers, setAnswersState] = useState<CommAnswer[]>([]);
  const answersRef = useRef<CommAnswer[]>([]);
  const setAnswers = useCallback((next: CommAnswer[]) => {
    answersRef.current = next;
    setAnswersState(next);
  }, []);
  const [rosterCount, setRosterCount] = useState(0);

  const upsertAnswer = useCallback(
    (a: CommAnswer) => {
      const prev = answersRef.current;
      const i = prev.findIndex((x) => x.memberId === a.memberId);
      const next = i < 0 ? [...prev, a] : prev.map((x, k) => (k === i ? a : x));
      setAnswers(next);
    },
    [setAnswers]
  );

  /** 현재 열린 질문 + 응답 (일괄 저장·탭 닫힘 대비) */
  const snapshot = useCallback((): LessonCommQuestionSnapshot | null => {
    const q = questionRef.current;
    return q ? { question: q, answers: answersRef.current.slice() } : null;
  }, []);

  useEffect(() => {
    handlersRef.current = {
      onWelcome: (items: CommItem[], ans: Record<string, CommAnswer[]>) => {
        const q = questionRef.current;
        if (q && items.some((it) => it.id === q.id && !it.closed)) setAnswers(ans[q.id] ?? []);
      },
      onAnswer: (requestId: string, a: CommAnswer) => {
        const q = questionRef.current;
        if (!q || q.id !== requestId) return;
        upsertAnswer(a);
        onStudentAnswerRef.current?.(requestId, a);
      },
    };
  }, [handlersRef, upsertAnswer, setAnswers]);

  /* 명단 동기화: QR 을 누른 세션에서, 연결된 뒤 명단 서명이 지난번 보낸 것과 다를 때만 (비어 있으면 보내지 않음 → 방 코드 입장 유지) */
  const rosterClean = useMemo(
    () =>
      roster
        .filter((r) => Number.isInteger(r.no) && r.no >= 1 && r.no <= 9999 && ENTRY_RE.test(r.entry))
        .slice(0, 100),
    [roster]
  );
  const rosterSig = useMemo(() => rosterClean.map((r) => `${r.no}:${r.entry}`).join(','), [rosterClean]);
  const lastRosterSentRef = useRef('');
  useEffect(() => {
    if (!activated || !connected || !rosterClean.length || !room?.roomId) return;
    const key = `${room.roomId}|${rosterSig}`;
    if (lastRosterSentRef.current === key) return;
    if (send({ t: 'roster', entries: rosterClean })) {
      lastRosterSentRef.current = key;
      setRosterCount(rosterClean.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activated, connected, rosterSig, room?.roomId]);

  /* 방 제목 = 현재 수업 프레젠: 질문 QR 을 누른 뒤 연결되면, 그리고 프레젠이 바뀌면 갱신 (폴링 없음) */
  const wantTitle = className.trim().slice(0, 40) || '우리 반';
  const renameSentRef = useRef('');
  useEffect(() => {
    if (!activated || !connected || !room) return;
    /* 연결(재연결) 때마다 한 번은 보내 서버의 프레젠 키를 맞춤 — 값이 같으면 서버가 무시 */
    const sig = `${room.roomId}|${wantTitle}|${presKey}`;
    if (renameSentRef.current === sig) return;
    renameSentRef.current = sig;
    host.rename(wantTitle, presKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activated, connected, room?.roomId, wantTitle, presKey]);
  useEffect(() => {
    if (!connected) renameSentRef.current = '';
  }, [connected]);

  /** 방이 없으면 만들기. 반환: 오류 코드(null = 성공/이미 있음) */
  const ensureRoom = useCallback(async (): Promise<string | null> => {
    if (room) return null;
    if (!canUse) return 'not_paid';
    const cls = className.trim().slice(0, 40) || '우리 반';
    return host.create({ className: cls, teacherName: teacherName || '선생님', entryCode: randomEntryCode() });
  }, [room, canUse, className, teacherName, host]);

  /** 학생 기기에 질문 보내기 (이전 질문은 서버가 교체) */
  const sendQuestion = useCallback(
    (q: LessonCommQuestion, src?: 'page' | 'remote'): boolean => {
      const prev = questionRef.current;
      if (prev && prev.id !== q.id) onQuestionEndedRef.current?.({ question: prev, answers: answersRef.current.slice() });
      questionRef.current = q;
      setQuestion(q);
      setAnswers([]);
      return send({
        t: 'question',
        id: q.id,
        text: q.text.slice(0, 300),
        questionType: q.questionType,
        ...(q.questionType === 'choice' && q.choices?.length ? { choices: q.choices } : {}),
        ...(src ? { src } : {}),
      });
    },
    [send, setAnswers]
  );
  const closeQuestion = useCallback(
    (id?: string) => {
      const q = questionRef.current;
      if (!q || (id && q.id !== id)) return;
      send({ t: 'close', id: q.id });
      onQuestionEndedRef.current?.({ question: q, answers: answersRef.current.slice() });
      questionRef.current = null;
      setQuestion(null);
    },
    [send]
  );

  const studentLink = room ? commStudentLink(room.roomId) : '';
  const onlineCount = members.filter((m) => m.online).length;
  const answerRows = useMemo(
    () =>
      answers
        .slice()
        .sort((a, b) => (a.number ?? 99999) - (b.number ?? 99999) || a.at - b.at)
        .map((a) => ({ ...a, label: commMemberLabel(a, a.number != null ? rosterNames?.get(a.number) : undefined) })),
    [answers, rosterNames]
  );

  return {
    available: host.available,
    room,
    status: host.status,
    connected,
    members,
    onlineCount,
    rosterCount,
    rosterMode: rosterClean.length > 0,
    studentLink,
    ensureRoom,
    endRoom: host.end,
    reconnect: host.reconnect,
    question,
    answers: answerRows,
    sendQuestion,
    closeQuestion,
    snapshot,
  };
}
